// Frames (plan step 3.4; spec 8.5; Console D, the mock's #/perf/frames): slow and frozen frames per
// minute in the foreground, the worst screen, per screen and per release, and slow frames per
// minute over the days. Per-minute counts compare across platforms; the share of slow frames
// compares within one platform only, because iOS counts the display's idle refreshes in the
// total, and the page says so.
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useState } from 'react'
import { framesQuery, seriesQuery } from '../api/queries'
import type { FrameScreen, Frames } from '../api/types'
import {
  EmptyNote,
  HovRow,
  Pill,
  Section,
  SortTh,
  Strip,
  type StripItem,
  Table,
} from '../components/d'
import { LineChart } from '../components/LineChart'
import {
  Delta,
  deltaMeta,
  NoApp,
  PerfHead,
  perfMeta,
  ReleaseLink,
  releaseMarker,
  usePerfReleases,
  usePerfScope,
  withRelease,
} from '../components/Perf'
import { ErrorState, Loading } from '../components/states'
import { type Translator, useI18n } from '../i18n'
import { perMinute as formatPerMinute } from '../lib/format'
import { useProjectParams } from './params'
import { carried, type FRAME_SORTS, type ListSearch } from './search'

/** Below a minute in the foreground, a screen's rates are noisy (the API ranks them last). */
const MIN_MINUTES = 1

type FrameSort = (typeof FRAME_SORTS)[number]

const perMinute = (i18n: Translator, v: number | null | undefined) =>
  v === null || v === undefined ? '—' : formatPerMinute(i18n, v)
const share = (i18n: Translator, v: number | null | undefined) =>
  v === null || v === undefined ? '—' : i18n.percent(v, 1)
/** Foreground time in hours, the mock's unit: 5,924 h. */
const hours = (i18n: Translator, minutes: number) =>
  i18n.number(minutes / 60, { maximumFractionDigits: minutes < 600 ? 1 : 0 })

/** The worst screen: the most slow frames per minute among those with a minute or more. */
export function worstScreen(screens: FrameScreen[]): FrameScreen | undefined {
  let worst: FrameScreen | undefined
  for (const s of screens)
    if (
      s.screen &&
      s.foreground_minutes >= MIN_MINUTES &&
      (s.slow_per_minute ?? -1) > (worst?.slow_per_minute ?? -1)
    )
      worst = s
  return worst
}

export function FramesPage() {
  const i18n = useI18n()
  const { t, number, date } = i18n
  const { orgId, projectId } = useProjectParams()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as ListSearch
  const { scope, app, appsPending } = usePerfScope()
  const { list, current, previous } = usePerfReleases(scope)
  // Screen and slow share sort here; the other columns are the API's sorts.
  const [local, setLocal] = useState<'screen' | 'share' | undefined>()
  const sort = (search.sort as FrameSort | undefined) ?? 'minutes'
  const enabled = !!scope.app_id
  const frames = useQuery({ ...framesQuery(projectId, scope, sort), enabled })
  // With one release in scope, the one before it; with every release, the API's release rows.
  const before = useQuery({
    ...framesQuery(projectId, withRelease(scope, previous), 'minutes', 1),
    enabled: enabled && !!scope.release && !!previous,
  })
  const series = useQuery({
    ...seriesQuery(
      projectId,
      scope.app_id ?? '',
      'slow_frames_per_minute',
      scope.days,
      'day',
      scope,
    ),
    enabled,
  })
  const params = { orgId, projectId }
  const keep = { ...carried(search), app: scope.app_id }
  const title = t('perf.frames.title')
  const page = t('perf.nav.frames')

  if (appsPending)
    return (
      <main id="main" className="pg pf-pg">
        <Loading />
      </main>
    )
  if (!app)
    return (
      <main id="main" className="pg pf-pg">
        <PerfHead title={title} page={page} />
        <NoApp />
      </main>
    )

  const f = frames.data
  const meta = perfMeta(i18n, app, scope, [
    t('perf.frames.metaUnit'),
    deltaMeta(t, scope, current, previous),
  ])
  const rows = (data: Frames | undefined, release: string | undefined) =>
    data?.releases.find((r) => r.release === release)
  const cur = scope.release ? f?.totals : rows(f, current)
  const was = scope.release ? before.data?.totals : rows(f, previous)
  const diff = (k: 'slow_per_minute' | 'frozen_per_minute') =>
    cur && was && cur[k] !== null && was[k] !== null
      ? (cur[k] as number) - (was[k] as number)
      : null
  const worst = f ? worstScreen(f.screens) : undefined
  const fmt = (v: number) => formatPerMinute(i18n, v)

  const strip = (data: Frames): StripItem[] => [
    {
      dt: t('perf.frames.slowShort'),
      dd: perMinute(i18n, data.totals.slow_per_minute),
      small: '/min',
      d: (
        <>
          {previous ? (
            <>
              <Delta value={diff('slow_per_minute')} format={fmt} /> ·{' '}
            </>
          ) : null}
          {t('perf.frames.ofFrames', { share: share(i18n, data.totals.slow_share) })}
        </>
      ),
    },
    {
      dt: t('perf.frames.frozenShort'),
      dd: perMinute(i18n, data.totals.frozen_per_minute),
      small: '/min',
      d: [
        t('perf.frames.frozenCount', { count: data.totals.frozen }),
        previous && was ? `${previous} ${perMinute(i18n, was.frozen_per_minute)}` : '',
      ]
        .filter(Boolean)
        .join(' · '),
    },
    {
      dt: t('perf.frames.foregroundShort'),
      dd: hours(i18n, data.totals.foreground_minutes),
      small: 'h',
      d: t('perf.frames.sessions', { sessions: number(data.totals.sessions) }),
    },
    ...(worst
      ? [
          {
            dt: t('perf.frames.worst'),
            dd: perMinute(i18n, worst.slow_per_minute),
            small: '/min',
            d: (
              <span translate="no">
                {t('perf.frames.worstLine', {
                  screen: worst.screen,
                  share: share(i18n, worst.slow_share),
                })}
              </span>
            ),
            link: ({ className, children }) => (
              <Link
                className={className}
                to="/o/$orgId/p/$projectId/performance/screens/detail"
                params={params}
                search={{ ...keep, screen: worst.screen }}
              >
                {children}
              </Link>
            ),
          } satisfies StripItem,
        ]
      : []),
  ]

  // No foreground time in scope: the page keeps its shape (APS-385), a dash in each cell and no
  // unit beside it, each section saying it has nothing, as the overview and Hangs do.
  const empty = !f || f.totals.foreground_minutes === 0
  const emptyStrip: StripItem[] = [
    { dt: t('perf.frames.slowShort'), dd: '—' },
    { dt: t('perf.frames.frozenShort'), dd: '—' },
    { dt: t('perf.frames.foregroundShort'), dd: '—' },
  ]

  const setSort = (s: FrameSort) => {
    setLocal(undefined)
    return navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        sort: s === 'minutes' ? undefined : s,
      }),
    })
  }
  const head = (s: FrameSort, label: string) => (
    <SortTh on={!local && sort === s} onSort={() => setSort(s)} className="num">
      {label}
    </SortTh>
  )
  const rowsOf = (list: FrameScreen[]) =>
    local === 'screen'
      ? [...list].sort((a, b) => a.screen.localeCompare(b.screen))
      : local === 'share'
        ? [...list].sort((a, b) => (b.slow_share ?? -1) - (a.slow_share ?? -1))
        : list

  return (
    <main id="main" className="pg pf-pg">
      <PerfHead title={title} page={page} meta={meta} />
      {frames.isPending ? (
        <Loading />
      ) : frames.isError ? (
        <ErrorState error={frames.error} retry={() => frames.refetch()} />
      ) : (
        <>
          <Strip items={!f || empty ? emptyStrip : strip(f)} translate="no" />
          <div className="grid2">
            <Section
              title={t('perf.frames.daily')}
              meta={t('perf.frames.dailyMeta', {
                days: scope.days,
                release: scope.release ?? t('perf.meta.allReleases'),
              })}
            >
              <div className="pf-chart">
                {series.isPending ? (
                  <Loading />
                ) : series.isError ? (
                  <ErrorState error={series.error} retry={() => series.refetch()} />
                ) : series.data.points.every((p) => p.value === null) ? (
                  <EmptyNote title={t('perf.trend.empty')} />
                ) : (
                  <LineChart
                    label={t('perf.frames.dailyLabel', { days: scope.days })}
                    accent
                    marker={releaseMarker(
                      list,
                      current,
                      series.data.points.map((p) => p.key),
                    )}
                    points={series.data.points.map((p) => ({
                      label: date(`${p.key}T00:00:00Z`, {
                        month: 'short',
                        day: 'numeric',
                        timeZone: 'UTC',
                      }),
                      value: p.value,
                    }))}
                    format={(v) => `${perMinute(i18n, v)} /min`}
                  />
                )}
              </div>
            </Section>
            <Section title={t('perf.frames.releases')} meta={t('perf.frames.releasesSub')}>
              {!f || empty || f.releases.length === 0 ? (
                <EmptyNote title={t('perf.frames.releasesNone')} />
              ) : (
                <ul className="rl pf-rl">
                  {f.releases.map((r) => (
                    <li key={`${r.app_id} ${r.release}`}>
                      <ReleaseLink release={r.release} />
                      <span>
                        {t('perf.frames.releaseRates', {
                          slow: perMinute(i18n, r.slow_per_minute),
                          frozen: perMinute(i18n, r.frozen_per_minute),
                        })}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </div>
          <Section
            title={t('perf.frames.screens')}
            meta={local ? undefined : t(`perf.frames.sortMeta.${sort}`)}
          >
            {!f || empty || f.screens.length === 0 ? (
              <EmptyNote title={t('perf.frames.none')}>
                {empty ? t('perf.frames.noneBody') : null}
              </EmptyNote>
            ) : (
              <Table label={t('perf.frames.screens')} rowKeys className="pf-tbl">
                <thead>
                  <tr>
                    <SortTh on={local === 'screen'} onSort={() => setLocal('screen')}>
                      {t('vitals.col.screen')}
                    </SortTh>
                    {head('slow_per_minute', t('perf.frames.col.slow'))}
                    {head('frozen_per_minute', t('perf.frames.col.frozen'))}
                    <SortTh on={local === 'share'} onSort={() => setLocal('share')} className="num">
                      {t('perf.frames.col.share')}
                    </SortTh>
                    {head('minutes', t('perf.frames.col.time'))}
                  </tr>
                </thead>
                <tbody>
                  {rowsOf(f.screens).map((s) => {
                    const few = s.foreground_minutes < MIN_MINUTES
                    const isWorst = s === worst
                    const cells = (
                      <>
                        <td className="mono anywhere" translate="no">
                          {s.screen ? (
                            <Link
                              to="/o/$orgId/p/$projectId/performance/screens/detail"
                              params={params}
                              search={{ ...keep, screen: s.screen }}
                            >
                              {s.screen}
                            </Link>
                          ) : (
                            <span className="dim">{t('perf.hangs.noScreen')}</span>
                          )}
                        </td>
                        <td className="num">
                          {isWorst ? (
                            <Pill tone="warn">{perMinute(i18n, s.slow_per_minute)}</Pill>
                          ) : (
                            perMinute(i18n, s.slow_per_minute)
                          )}
                        </td>
                        <td className="num">
                          {isWorst ? (
                            <Pill tone="warn">{perMinute(i18n, s.frozen_per_minute)}</Pill>
                          ) : (
                            perMinute(i18n, s.frozen_per_minute)
                          )}
                        </td>
                        <td className="num">{share(i18n, s.slow_share)}</td>
                        <td className="num" translate="no">
                          {`${hours(i18n, s.foreground_minutes)} h`}
                        </td>
                      </>
                    )
                    return s.screen ? (
                      <HovRow
                        key={`${s.app_id} ${s.screen}`}
                        className={few ? 'dim' : undefined}
                        onOpen={() =>
                          navigate({
                            to: '/o/$orgId/p/$projectId/performance/screens/detail',
                            params,
                            search: { ...keep, screen: s.screen },
                          })
                        }
                      >
                        {cells}
                      </HovRow>
                    ) : (
                      <tr key={`${s.app_id} -`} className={few ? 'dim' : undefined}>
                        {cells}
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            )}
            <p className="pf-note">{t('perf.frames.foot')}</p>
          </Section>
        </>
      )}
    </main>
  )
}
